/**
 * Apollo result → ServerSnapshot. Pure; unit-tested.
 */
import { differenceInMinutes, parseISO } from "date-fns";
import type { HomeHealthDemoHorizonQuery } from "~/graphql/__generated__/graphql";
import type { BlockLike, CancellationReasonLike, DateKey, EpisodeLike, IsoString, PatientLike, ProviderLike, ServerSnapshot, ShiftLike, VisitResourceLike } from "@caredna-marlin/utils/homeHealthDemo/state";

export function normalizeHorizon(
  data: HomeHealthDemoHorizonQuery,
  fetchedAtIso: IsoString,
  rangeStart: DateKey,
  rangeEnd: DateKey,
): ServerSnapshot {
  const shifts: ShiftLike[] = data.shift.map((s) => ({
    id: s.id,
    providerId: s.providerId,
    startIso: s.startTime,
    endIso: s.endTime,
    status: s.status === "canceled" ? "canceled" : "confirmed",
    overflow: s.overflowShift,
    shiftCenterId: s.shiftCenterId ?? undefined,
    shiftCenterName: s.shiftCenter?.name ?? undefined,
    shiftCenterZip: s.shiftCenter?.addressZip ?? undefined,
    providerZip: s.provider?.addressZip ?? s.addressZip ?? undefined,
    useShiftCenterDriveTimes: s.useShiftCenterDriveTimes,
  }));

  const blocks: BlockLike[] = data.calendarBlock.map((b) => ({
    id: b.id,
    providerId: b.providerId,
    startIso: b.startTime,
    endIso: b.endTime,
    blockType: b.blockType,
    visitTypeIds: b.filters?.visitTypeIds ?? null,
    title: b.title ?? undefined,
    canceled: b.eventStatus === "canceled",
  }));

  const visitResources: VisitResourceLike[] = [];
  for (const vr of data.visitResource) {
    const visit = vr.visit;
    if (!visit) continue;
    visitResources.push({
      id: vr.id,
      visitId: vr.visitId,
      providerId: vr.providerId ?? undefined,
      startIso: vr.startTime,
      endIso: vr.endTime,
      durationMinutes: Math.max(0, differenceInMinutes(parseISO(vr.endTime), parseISO(vr.startTime))),
      startOfShiftDriveMinutes: vr.startOfShiftDriveTime ?? 0,
      interVisitDriveMinutes: vr.interVisitDriveTime ?? 0,
      endOfShiftDriveMinutes: vr.endOfShiftDriveTime ?? 0,
      visitTypeId: visit.visitTypeId,
      visitTypeName: visit.visitType?.name ?? "",
      patientId: visit.patient?.id ?? -1,
      patientName: visit.patient ? `${visit.patient.firstName} ${visit.patient.lastName}`.trim() : "",
      patientZip: visit.addressZip ?? visit.patient?.addressZip ?? undefined,
      eventStatus: visit.eventStatus,
      arrivalWindowStartIso: visit.arrivalWindowStartTime ?? undefined,
      arrivalWindowEndIso: visit.arrivalWindowEndTime ?? undefined,
    });
  }

  const providers: ProviderLike[] = data.commandIndexProviders.providers.map((p) => ({
    id: p.id,
    displayName: `${p.firstName} ${p.lastName}`.trim(),
    email: p.OrganizationUser?.user?.email ?? undefined,
    providerTypeName: p.providerType?.name ?? undefined,
    homeZip: p.addressZip ?? undefined,
    active: p.active,
  }));

  const patients: PatientLike[] = data.patient.map((p) => ({
    id: p.id,
    firstName: p.firstName,
    lastName: p.lastName,
    zip: p.addressZip ?? undefined,
    mrn: p.patientIdentifiers?.find((i) => i.identifier_type === "MRN")?.identifier ?? undefined,
  }));
  const episodes: EpisodeLike[] = data.episode.map((e) => ({ id: e.id, patientId: e.patientId, active: e.active }));
  const cancellationReasons: CancellationReasonLike[] = data.visitCancellationReason.map((r) => ({ id: r.id, description: r.description }));

  return { fetchedAtIso, rangeStart, rangeEnd, shifts, blocks, visitResources, providers, patients, episodes, cancellationReasons };
}

/** Stable fingerprint of the data (not the fetch time) so identical polls do not churn the store. */
export function snapshotFingerprint(snapshot: ServerSnapshot): string {
  const { fetchedAtIso: _fetchedAt, ...rest } = snapshot;
  return JSON.stringify(rest);
}
